/**
 * useProductSubmissionV3 - Submission hook for V3 product editor
 *
 * Handles API submission for product creation and updates.
 * Manages file uploads, batch operations, and progress tracking.
 */

import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  ProductFormStateV3,
  UseProductSubmissionV3Return,
  SubmissionProgress,
  SubmissionResult,
  SubmissionStatus,
  VariantV3,
  createDefaultSubmissionProgress,
  isDimensionValue,
  isColorAxisValue,
} from '../types';
import type {
  ProductV2,
  CreateProductV2Payload,
  UpdateProductV2Payload,
  BulkOperationItem,
  ProductAxes,
  ColorAxis,
  SizeAxis,
} from '../../../../common/models/product-v2.model';
import type { ProductFamily } from '../../../../common/models/product-family.model';
import { SURFACE_FINISHES, type SurfaceFinish } from '../../../../common/models/product-v2.model';
import { productV2Api } from '../api/productV2Api';
import { base64ToBlob } from '../../../../common/utils/utils';
import { extractDominantColors } from '../utils/colorExtraction';
import { pickLayoutOptions } from '../../../../common/utils/pattern-name';
import { plankCm } from '../../../../common/utils/units';
import { commerceMoneyForPayload } from '../utils/pricePackaging';

// ============================================================================
// Types
// ============================================================================

export interface UseProductSubmissionV3Options {
  /** Product form state */
  product: ProductFormStateV3 | null;
  /** Variants to submit */
  variants: VariantV3[];
  /** Product family configuration */
  family: ProductFamily;
  /** Uploaded plank images keyed by plankGroupId */
  uploadedPlankImages?: { [plankGroupId: string]: File[] };
  /** Original variant UUIDs for detecting deletions */
  originalVariantUuids?: Set<string>;
  /** Callback on successful submission */
  onSuccess?: (result: SubmissionResult) => void;
  /** Callback on submission error */
  onError?: (error: Error) => void;
}

export interface FileUpload {
  /** The file to upload */
  file: File;
  /**
   * File type:
   * - `thumbnail`: product thumbnail image
   * - `material`: source image for surface seamless generation
   * - `plank`: individual plank photo (part of a plankGroup)
   * - `model`: primary 3D model (GLTF/GLB) — used by Android AR + 3D modal + visualizer
   * - `originalImage`: full-resolution product image
   * - `usdz`: iOS AR Quick Look asset (USDZ) — required for AR on iPhones/iPads
   */
  type: 'thumbnail' | 'material' | 'plank' | 'model' | 'originalImage' | 'usdz';
  /** Associated variant UUID */
  variantUuid: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Remove visualization sub-objects that don't match the visualization type.
 * e.g. a model3d product should not have a `surface` key.
 */
function stripIrrelevantVisualization(
  visualization: VariantV3['visualization']
): VariantV3['visualization'] {
  if (!visualization) return visualization;
  const cleaned = { ...visualization };
  if (cleaned.type !== 'surface') {
    delete cleaned.surface;
  }
  if (cleaned.type !== 'model3d') {
    delete cleaned.model3d;
  }
  return cleaned;
}

/**
 * The product-level "shoppers may change the grout" switch, when it was set.
 */
export function productGroutEditable(product: any): boolean | undefined {
  const value = product?.visualization?.surface?.groutEditable;
  return typeof value === 'boolean' ? value : undefined;
}

/**
 * The product-level finish (how it reflects light), when it was set.
 */
export function productFinish(product: any): SurfaceFinish | undefined {
  const value = product?.visualization?.surface?.finish;
  return SURFACE_FINISHES.includes(value) ? value : undefined;
}

/**
 * Unchanged variants whose grout switch or finish differs from the product's:
 * they must be saved too, or the setting reaches only the variants edited
 * alongside it.
 */
export function variantsMissingSurfaceSettings(product: any, variants: VariantV3[]): VariantV3[] {
  const groutEditable = productGroutEditable(product);
  const finish = productFinish(product);
  return variants.filter((v) =>
    v.visualization?.type === 'surface' && (
      (groutEditable !== undefined && !!v.visualization?.surface?.groutEditable !== groutEditable)
      || (finish !== undefined && (v.visualization?.surface?.finish || 'matte') !== finish)
    )
  );
}

/**
 * Check if a string is base64 data URL
 */
function isBase64DataUrl(str: string | undefined | null): boolean {
  if (!str || typeof str !== 'string') return false;
  return str.startsWith('data:');
}

/**
 * Extract files from variant visualization and return cleaned variant.
 * Converts base64 data URLs to File objects for upload.
 * Also extracts dominant colors from the first available image file.
 */
async function extractFilesFromVariant(
  variant: VariantV3
): Promise<{ files: FileUpload[]; cleanedVisualization: VariantV3['visualization'] }> {
  const files: FileUpload[] = [];
  const cleanedVisualization = variant.visualization
    ? JSON.parse(JSON.stringify(variant.visualization))
    : undefined;

  if (!cleanedVisualization) {
    return { files, cleanedVisualization };
  }

  // Extract thumbnail if it's base64
  if (isBase64DataUrl(cleanedVisualization.thumbnailPath)) {
    try {
      const blob = base64ToBlob(cleanedVisualization.thumbnailPath);
      const file = new File([blob], `thumbnail_${variant.uuid}.png`, { type: 'image/png' });
      files.push({
        file,
        type: 'thumbnail',
        variantUuid: variant.uuid,
      });
      // Clear base64 from payload - backend will populate from uploaded file
      cleanedVisualization.thumbnailPath = undefined;
    } catch (err) {
      console.error('[useProductSubmissionV3] Failed to convert thumbnail base64:', err);
    }
  }

  // Extract material/texture if it's base64
  if (cleanedVisualization.surface && isBase64DataUrl(cleanedVisualization.surface.materialPath)) {
    try {
      const blob = base64ToBlob(cleanedVisualization.surface.materialPath);
      const file = new File([blob], `image_${variant.uuid}.png`, { type: 'image/png' });
      files.push({
        file,
        type: 'material',
        variantUuid: variant.uuid,
      });
      // Clear base64 from payload - backend will populate from uploaded file
      cleanedVisualization.surface.materialPath = undefined;
    } catch (err) {
      console.error('[useProductSubmissionV3] Failed to convert material base64:', err);
    }
  }

  // Extract dominant colors from the first available image (material > originalImage > thumbnail)
  const imageFile = files.find((f) => f.type === 'material')
    || files.find((f) => f.type === 'originalImage')
    || files.find((f) => f.type === 'thumbnail');
  if (imageFile) {
    try {
      const colors = await extractDominantColors(imageFile.file);
      cleanedVisualization.extractedColors = colors;
    } catch (err) {
      // Color extraction is best-effort — don't block submission
      console.error('[useProductSubmissionV3] Failed to extract colors:', err);
    }
  }

  // Clean up empty string paths so backend doesn't misinterpret them as actual values.
  // createDefaultVisualization sets these to "" but the backend expects undefined/null for "no value".
  if (cleanedVisualization.thumbnailPath === '') {
    cleanedVisualization.thumbnailPath = undefined;
  }
  if (cleanedVisualization.surface?.materialPath === '') {
    cleanedVisualization.surface.materialPath = undefined;
  }
  if (cleanedVisualization.model3d?.originalImagePath === '') {
    cleanedVisualization.model3d.originalImagePath = undefined;
  }
  if (cleanedVisualization.model3d?.modelPath === '') {
    cleanedVisualization.model3d.modelPath = undefined;
  }

  return { files, cleanedVisualization };
}

/**
 * Strip inherited multiple-mode data from single-mode variants.
 * Mutates cleanedVisualization in place before it reaches the payload.
 */
function stripSingleModeInheritedData(
  variant: VariantV3,
  cleanedVisualization: VariantV3['visualization'],
): void {
  if (variant.visualization?.surface?.isMultiple !== false) return;
  const surface = cleanedVisualization?.surface;
  if (!surface) return;
  surface.materialPaths = [];
  delete surface.plankGroupId;
}

/**
 * Determine if backend seamless generation is needed and build params.
 * When a variant has actual plank images but no frontend-generated
 * seamless texture (materialPath), the worker should generate the seamless.
 *
 * Single-mode variants (those with a seamless texture but no planks) are never
 * candidates for generation — having a plankGroupId alone isn't enough.
 */
function buildSeamlessConfig(
  variant: VariantV3,
  uploadedPlankImages?: { [key: string]: File[] },
  forceRegenerate?: boolean,
): { generateSeamless: boolean; seamlessParams?: BulkOperationItem['seamlessParams'] } {
  const surface = variant.visualization?.surface;
  const hasMaterialPath = !!surface?.materialPath;
  const plankGroupId = surface?.plankGroupId;

  // Single-mode variants upload a ready-to-use seamless texture directly —
  // they should NEVER trigger backend seamless generation.
  if (!surface?.isMultiple) {
    return { generateSeamless: false };
  }

  // Check if there are actual plank images to generate from (existing or newly uploaded)
  const hasExistingPlanks = (surface?.materialPaths?.length || 0) > 0;
  const hasUploadedPlanks = !!(plankGroupId && uploadedPlankImages?.[plankGroupId]?.length);
  const hasPlanks = hasExistingPlanks || hasUploadedPlanks;

  // Skip if: no plank group, no actual plank images,
  // or seamless already exists (unless forceRegenerate due to grout/size/pattern change)
  if (!plankGroupId || !hasPlanks || (hasMaterialPath && !forceRegenerate)) {
    return { generateSeamless: false };
  }

  // Build seamlessParams from variant data for backend generation
  const sizeAxis = variant.axes?.size;
  // The generator works in cm, whatever unit the size was entered in.
  const unit = (sizeAxis && typeof sizeAxis === 'object' && 'unit' in sizeAxis) ? (sizeAxis as any).unit : 'cm';
  const width = (sizeAxis && typeof sizeAxis === 'object' && 'width' in sizeAxis) ? plankCm((sizeAxis as any).width, unit) : 60;
  const height = (sizeAxis && typeof sizeAxis === 'object' && 'height' in sizeAxis) ? plankCm((sizeAxis as any).height, unit) : 60;
  const pattern = (typeof variant.axes?.pattern === 'string') ? variant.axes.pattern : 'stackbond';

  return {
    generateSeamless: true,
    seamlessParams: {
      pattern,
      groutLineColor: surface?.grout?.color || '#00000000',
      groutLineWidth: surface?.grout?.width || 0,
      plankWidth: width,
      plankHeight: height,
      // Mirror, row offset, flip… as the product sets them (CSV "stackbond:mirror").
      ...pickLayoutOptions(surface?.seamlessParams),
    },
  };
}

/**
 * Ensure every surface product has a complete seamlessParams object with all
 * required fields: zoom, crop, pattern.
 * Called unconditionally for all surface products (not just when generating).
 */
function applyDefaultSeamlessParams(payload: CreateProductV2Payload | UpdateProductV2Payload): void {
  const surface = (payload as any).visualization?.surface;
  if (!surface) return;
  if (!surface.seamlessParams) surface.seamlessParams = {};
  if (surface.seamlessParams.zoom == null) surface.seamlessParams.zoom = 5;
  if (surface.seamlessParams.crop == null) surface.seamlessParams.crop = { x: 0, y: 0 };
  // The variant's pattern axis is the truth: a pattern changed in the editor
  // used to leave the old name here, and whatever regenerated the texture
  // from these params (content studio) laid the old pattern.
  const axisPattern = (payload as any).axes?.pattern;
  if (typeof axisPattern === 'string' && axisPattern) {
    surface.seamlessParams.pattern = axisPattern;
  } else if (!surface.seamlessParams.pattern) {
    surface.seamlessParams.pattern = 'stackbond';
  }
}

/**
 * Convert VariantV3 to ProductV2 payload
 * @param variant - The variant to convert
 * @param baseProduct - The base product form state
 * @param isNew - Whether this is a new variant (create) or existing (update)
 * @param cleanedVisualization - Optional visualization with base64 data stripped out
 * @param publishDraft - When true, force `isDraft: false` + `isVisible: true` to
 *                       publish a previously-synced draft. When false/undefined,
 *                       preserves the current state on the form.
 */
function variantToProductPayload(
  variant: VariantV3,
  baseProduct: ProductFormStateV3,
  isNew: boolean,
  cleanedVisualization?: VariantV3['visualization'],
  publishDraft?: boolean,
  family?: ProductFamily,
): CreateProductV2Payload | UpdateProductV2Payload {
  // Convert axes from AxisValue to ProductAxes format
  const axes: ProductAxes = {};

  for (const [name, value] of Object.entries(variant.axes)) {
    if (value === null || value === undefined) continue;

    if (name === 'color' || name === 'label') {
      if (isColorAxisValue(value)) {
        axes[name] = value as ColorAxis;
      } else if (typeof value === 'string') {
        axes[name] = { value, hex: undefined };
      }
    } else if (name === 'size') {
      if (isDimensionValue(value)) {
        axes.size = {
          width: value.width,
          height: value.height,
          depth: value.depth,
          unit: value.unit,
        } as SizeAxis;
      }
    } else if (name === 'pattern') {
      if (typeof value === 'string') {
        axes.pattern = value;
      }
    } else {
      // Custom axis - store directly
      axes[name] = value as any;
    }
  }

  // Visualization — cleaned (no base64), irrelevant sub-objects stripped.
  const visualization = stripIrrelevantVisualization(
    cleanedVisualization !== undefined ? cleanedVisualization : variant.visualization
  );
  // Placement (floor/wall) is edited at the product level so it works before any
  // variants exist; stamp it onto every variant's model3d so it persists.
  if (visualization?.type === 'model3d') {
    const arPlacement = baseProduct.visualization?.model3d?.arPlacement;
    if (arPlacement) {
      visualization.model3d = { ...(visualization.model3d || {}), arPlacement };
    }
  }
  // Whether shoppers may change the grout is product-level too, and so is the finish.
  if (visualization?.type === 'surface') {
    const groutEditable = productGroutEditable(baseProduct);
    if (groutEditable !== undefined) {
      visualization.surface = { ...(visualization.surface || {}), groutEditable };
    }
    const finish = productFinish(baseProduct);
    if (finish !== undefined) {
      visualization.surface = { ...(visualization.surface || {}), finish };
    }
  }

  const payload: CreateProductV2Payload = {
    // Core identity
    id: baseProduct.id || uuidv4(),
    uuid: variant.uuid,
    vendor: baseProduct.vendor,

    // Family — fall back to the currently selected family when the stored
    // product has none (e.g. drafts created by an integration sync before the
    // vendor picked a family in the editor).
    familyId: baseProduct.familyId ?? family?._id,
    familySlug: baseProduct.familySlug ?? family?.slug,

    // Furniture category (product-level; arPlacement rides in visualization.model3d)
    category: baseProduct.category,

    // Basic info
    name: baseProduct.name,
    brand: baseProduct.brand,
    description: baseProduct.description,
    sku: variant.sku,

    // Axes
    axes,

    // Visualization (cleaned above, with product-level placement stamped in)
    visualization,

    // Gallery images — vendor-uploaded contextual/lifestyle shots, or URLs
    // carried over from an API integration sync. Pending uploads live in
    // `_galleryUploads` and are sent as multipart files; the backend appends
    // their uploaded URLs to this array during bulk processing.
    galleryImages: variant.galleryImages ?? [],

    // Attributes
    attributes: baseProduct.attributes || {},

    // Organization
    organization: baseProduct.organization || {
      productType: '',
      applicationTypes: [],
    },

    // Commerce - variant-level wins, product-level as fallback for empty fields.
    // Price, packaging and sample are sent so edits persist (null clears); an
    // existing variant with no value of its own sends none, which keeps what
    // is stored. The read-only `pricing` is never sent.
    commerce: {
      link: variant.commerce?.link || baseProduct.commerce?.link || '',
      addToCartLink: variant.commerce?.addToCartLink || baseProduct.commerce?.addToCartLink || '',
      ...commerceMoneyForPayload(variant.commerce, baseProduct.commerce, isNew),
    },

    // State — when publishing a draft, force it visible and clear the draft
    // flag. On a normal save, preserve whatever state the product had (a
    // draft stays a draft and hidden). Existing non-draft products default
    // to visible, matching previous behaviour.
    isDefault: variant.isDefault,
    isVisible: publishDraft ? true : (baseProduct.isVisible ?? true),
    isDraft: publishDraft ? false : (baseProduct.isDraft ?? false),
    active: true,
    siblingOrder: variant.siblingOrder ?? 0,
    siblings: [],
  };

  if (isNew) {
    return payload;
  }

  // For updates, only include changed fields
  // Return the full payload for simplicity - API will handle merging
  return payload as UpdateProductV2Payload;
}

/**
 * Detect whether seamless-affecting inputs (grout, pattern, size) changed
 * compared to the variant's original snapshot. Used to force regeneration
 * on edit even when a materialPath already exists.
 */
function didSeamlessInputsChange(variant: VariantV3): boolean {
  if (!variant._originalSnapshot) return false;
  try {
    const original = JSON.parse(variant._originalSnapshot);
    const origSurface = original.visualization?.surface;
    const currSurface = variant.visualization?.surface;

    // Compare grout settings
    if (createStableSnapshot(origSurface?.grout) !== createStableSnapshot(currSurface?.grout)) return true;

    // Compare pattern axis
    if (createStableSnapshot(original.axes?.pattern) !== createStableSnapshot(variant.axes?.pattern)) return true;

    // Compare size axis
    if (createStableSnapshot(original.axes?.size) !== createStableSnapshot(variant.axes?.size)) return true;

    return false;
  } catch {
    return false;
  }
}

/**
 * Create a stable JSON string that's order-independent
 * Sorts object keys recursively before stringifying
 */
function createStableSnapshot(obj: unknown): string {
  return JSON.stringify(obj, (_, value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Sort object keys for consistent ordering
      const sorted: Record<string, unknown> = {};
      Object.keys(value).sort().forEach(key => {
        sorted[key] = value[key];
      });
      return sorted;
    }
    return value;
  });
}

/**
 * Strip cache-busting query params (e.g. ?v=1234567890) from a URL.
 * These are appended by reload() to force fresh browser fetches and must
 * be ignored during snapshot comparison.
 */
function stripCacheBuster(url: string | undefined | null): string | undefined | null {
  if (!url || typeof url !== 'string') return url;
  return url.replace(/[?&]v=\d+/g, '').replace(/\?$/, '');
}

/**
 * Determine which variants are new, modified, or deleted
 * @param currentVariants - Current variants in the form
 * @param originalSnapshots - Snapshots of original variant data for comparison
 * @param originalVariantUuids - Set of UUIDs that existed when the product was loaded (for deletion detection)
 */
function categorizeVariants(
  currentVariants: VariantV3[],
  originalSnapshots: Map<string, string>,
  originalVariantUuids?: Set<string>
): {
  created: VariantV3[];
  modified: VariantV3[];
  deleted: string[];
  unchanged: VariantV3[];
} {
  const created: VariantV3[] = [];
  const modified: VariantV3[] = [];
  const unchanged: VariantV3[] = [];
  const deleted: string[] = [];

  // Track which originals we've seen in current variants
  const seenUuids = new Set<string>();

  for (const variant of currentVariants) {
    seenUuids.add(variant.uuid);

    if (!variant.isSelected) {
      // Unselected variants are treated as deleted if they existed before
      if (originalSnapshots.has(variant.uuid) || originalVariantUuids?.has(variant.uuid)) {
        deleted.push(variant.uuid);
      }
      continue;
    }

    const originalSnapshot = originalSnapshots.get(variant.uuid);

    // A variant whose uuid was present when the product loaded always maps to an
    // existing DB document, so it must be an UPDATE — never a CREATE. Without
    // this guard a lost snapshot / stale `_isNew` flag (e.g. after an axis-value
    // edit regenerates the variant object) would emit a "create" for a document
    // that already exists, duplicating the product.
    const isKnownDocument = originalVariantUuids?.has(variant.uuid) ?? false;

    if (!originalSnapshot || variant._isNew) {
      if (isKnownDocument) {
        // Existing document with a missing/stale snapshot — force an update so
        // we modify the existing product instead of creating a duplicate.
        modified.push(variant);
      } else {
        // Genuinely new variant
        created.push(variant);
      }
    } else {
      // Check if modified using stable snapshot (order-independent comparison)
      // IMPORTANT: Exclude thumbnailPath as it gets converted to base64 by the UI for display
      // IMPORTANT: Strip cache-busting params from materialPath (appended by reload())
      const visualizationForSnapshot = variant.visualization ? {
        ...variant.visualization,
        thumbnailPath: undefined, // Exclude from comparison
        surface: variant.visualization.surface ? {
          ...variant.visualization.surface,
          materialPath: stripCacheBuster(variant.visualization.surface.materialPath),
        } : variant.visualization.surface,
      } : variant.visualization;

      const currentSnapshot = createStableSnapshot({
        axes: variant.axes,
        sku: variant.sku,
        isSelected: variant.isSelected,
        isDefault: variant.isDefault,
        visualization: visualizationForSnapshot,
        commerce: variant.commerce,
        siblingOrder: variant.siblingOrder,
      });

      if (currentSnapshot !== originalSnapshot) {
        modified.push(variant);
      } else {
        unchanged.push(variant);
      }
    }
  }

  // Find deleted variants: those in originalVariantUuids but not in current variants
  // Use originalVariantUuids if provided (accounts for variants removed from array)
  // Fall back to originalSnapshots.keys() for backwards compatibility
  const originalUuidsToCheck = originalVariantUuids || new Set(originalSnapshots.keys());
  originalUuidsToCheck.forEach((uuid) => {
    if (!seenUuids.has(uuid)) {
      deleted.push(uuid);
    }
  });

  return { created, modified, deleted, unchanged };
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useProductSubmissionV3(
  options: UseProductSubmissionV3Options
): UseProductSubmissionV3Return {
  const { product, variants, family, uploadedPlankImages, originalVariantUuids, onSuccess, onError } = options;

  // ─────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────

  const [progress, setProgress] = useState<SubmissionProgress>(
    createDefaultSubmissionProgress()
  );

  // Abort controller for cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Files to upload (collected during form editing)
  const pendingFilesRef = useRef<FileUpload[]>([]);

  // Original variant snapshots for comparison
  const originalSnapshotsRef = useRef<Map<string, string>>(new Map());

  // ─────────────────────────────────────────────────────────────────────────
  // Progress Helpers
  // ─────────────────────────────────────────────────────────────────────────

  const updateProgress = useCallback((updates: Partial<SubmissionProgress>) => {
    setProgress((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // File Upload
  // ─────────────────────────────────────────────────────────────────────────

  const addPendingFile = useCallback((upload: FileUpload) => {
    pendingFilesRef.current.push(upload);
  }, []);

  const setPendingFile = useCallback((upload: FileUpload) => {
    pendingFilesRef.current = pendingFilesRef.current.filter(
      f => !(f.variantUuid === upload.variantUuid && f.type === upload.type)
    );
    pendingFilesRef.current.push(upload);
  }, []);

  const clearPendingFiles = useCallback(() => {
    pendingFilesRef.current = [];
  }, []);

  /**
   * The file picked for a variant that hasn't been sent yet. Files only leave the
   * browser on save, so until then this is the only copy (e.g. a 3D model to preview).
   */
  const getPendingFile = useCallback(
    (variantUuid: string, type: FileUpload['type']): File | undefined =>
      pendingFilesRef.current.find(f => f.variantUuid === variantUuid && f.type === type)?.file,
    [],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Submission
  // ─────────────────────────────────────────────────────────────────────────

  const submit = useCallback(async (
    options?: { publishDraft?: boolean },
  ): Promise<SubmissionResult> => {
    const publishDraft = options?.publishDraft ?? false;
    if (!product) {
      const error = new Error('No product to submit');
      onError?.(error);
      return {
        success: false,
        productIds: [],
        errors: [{ message: error.message }],
      };
    }

    // Initialize progress
    updateProgress({
      status: 'validating',
      total: variants.filter((v) => v.isSelected).length,
      processed: 0,
      succeeded: 0,
      failed: 0,
    });

    // Create abort controller
    abortControllerRef.current = new AbortController();

    try {
      // Build original snapshots map from variants
      const originalSnapshots = new Map<string, string>();
      for (const variant of variants) {
        if (variant._originalSnapshot) {
          originalSnapshots.set(variant.uuid, variant._originalSnapshot);
        }
      }

      // Categorize variants
      // Pass originalVariantUuids to detect variants that were deleted (removed from array)
      const { created, modified, deleted, unchanged } = categorizeVariants(
        variants,
        originalSnapshots,
        originalVariantUuids
      );

      // If no variant-level changes but there ARE unchanged variants,
      // product-level fields must have changed (name, brand, organization, etc.)
      // since submit() is only callable when the form is dirty.
      // Force-update all unchanged variants to propagate product-level data.
      //
      // Publishing a draft also needs to force-update every variant so the
      // `isDraft: false` / `isVisible: true` flip reaches the DB even when
      // the form has no other changes.
      if (
        publishDraft ||
        (created.length === 0 && modified.length === 0 && deleted.length === 0 && unchanged.length > 0)
      ) {
        modified.push(...unchanged.splice(0));
      } else {
        const stale = new Set(variantsMissingSurfaceSettings(product, unchanged));
        if (stale.size > 0) {
          modified.push(...unchanged.filter((v) => stale.has(v)));
          unchanged.splice(0, unchanged.length, ...unchanged.filter((v) => !stale.has(v)));
        }
      }

      const totalOperations = created.length + modified.length + deleted.length;

      if (totalOperations === 0) {
        // Nothing to submit
        return {
          success: true,
          productIds: [],
          errors: [],
        };
      }

      updateProgress({
        status: 'submitting',
        total: totalOperations,
      });

      // Build bulk operations
      const operations: BulkOperationItem[] = [];

      // Collect files extracted from variants
      const extractedFiles: FileUpload[] = [];

      // Add created variants
      for (const variant of created) {
        // Extract base64 images, convert to files, and extract dominant colors
        const { files, cleanedVisualization } = await extractFilesFromVariant(variant);
        extractedFiles.push(...files);

        // Single-mode variants must never carry inherited plank data
        stripSingleModeInheritedData(variant, cleanedVisualization);

        const payload = variantToProductPayload(variant, product, true, cleanedVisualization, publishDraft, family);
        const seamlessConfig = buildSeamlessConfig(variant, uploadedPlankImages);
        if (seamlessConfig.generateSeamless) {
          applyDefaultSeamlessParams(payload);
        }
        operations.push({
          operation: 'create',
          data: payload as CreateProductV2Payload,
          ...seamlessConfig,
        });
      }

      // Add modified variants
      for (const variant of modified) {
        // Extract base64 images, convert to files, and extract dominant colors
        const { files, cleanedVisualization } = await extractFilesFromVariant(variant);
        extractedFiles.push(...files);

        // Single-mode variants must never carry inherited plank data
        stripSingleModeInheritedData(variant, cleanedVisualization);

        const payload = variantToProductPayload(variant, product, false, cleanedVisualization, publishDraft, family);
        const forceRegenerate = didSeamlessInputsChange(variant);
        const seamlessConfig = buildSeamlessConfig(variant, uploadedPlankImages, forceRegenerate);
        if (seamlessConfig.generateSeamless) {
          applyDefaultSeamlessParams(payload);
        }
        operations.push({
          operation: 'update',
          uuid: variant.uuid,
          data: payload as UpdateProductV2Payload,
          ...seamlessConfig,
        });
      }

      // Add deleted variants with metadata for UX display
      for (const uuid of deleted) {
        // Find the variant to get metadata (variant with isSelected: false is still in array)
        const deletedVariant = variants.find(v => v.uuid === uuid);
        const colorAxis = deletedVariant?.axes?.color;
        const sizeAxis = deletedVariant?.axes?.size;
        const patternAxis = deletedVariant?.axes?.pattern;

        operations.push({
          operation: 'delete',
          uuid,
          // Include metadata for progress display
          data: {
            name: product.name || 'Product',
            color: typeof colorAxis === 'object' && colorAxis !== null ? (colorAxis as any).value : colorAxis,
            size: sizeAxis && typeof sizeAxis === 'object'
              ? { width: (sizeAxis as any).width, height: (sizeAxis as any).height }
              : undefined,
            pattern: typeof patternAxis === 'string' ? patternAxis : undefined,
          } as any,
        });
      }

      // Build display-ready jobs from operations BEFORE submission.
      // The backend poll response lacks `uuid` on jobs and returns a raw UUID string
      // as `productData` for deletes, so we can't rely on the backend for display metadata.
      // Instead, we build complete jobs upfront and only merge status from backend by index
      // (operations and backend jobs are always in the same order).
      const displayJobs: NonNullable<SubmissionProgress['jobs']> = operations.map((op, index) => {
        const data = op.data as any;
        const uuid = op.uuid || data?.uuid;
        const name = data?.name || product.name || 'Product';

        let axes: Record<string, any> | undefined;
        if (op.operation === 'delete') {
          const deletedVariant = variants.find(v => v.uuid === uuid);
          axes = deletedVariant?.axes as any;
        } else {
          axes = data?.axes;
        }

        return {
          jobId: `pending-${index}`,
          uuid,
          operation: op.operation,
          status: 'pending' as string,
          productData: { name, axes },
        };
      });

      // Merge backend poll status into our display jobs (match by index)
      const mergeJobStatus = (backendJobs?: Array<Record<string, any>>): SubmissionProgress['jobs'] => {
        if (!backendJobs || backendJobs.length === 0) return displayJobs;
        return displayJobs.map((displayJob, index) => {
          const backendJob = backendJobs[index];
          if (!backendJob) return displayJob;
          return {
            ...displayJob,
            jobId: backendJob.jobId || displayJob.jobId,
            status: backendJob.status || displayJob.status,
            error: backendJob.error,
            errorDetails: backendJob.errorDetails,
            resultProduct: backendJob.resultProduct,
          };
        });
      };

      // Merge extracted files with any pending files from form interactions
      const allPendingFiles = [...pendingFilesRef.current, ...extractedFiles];

      // If only unchanged variants and no pending files, we're done
      if (operations.length === 0 && allPendingFiles.length === 0) {
        updateProgress({
          status: 'success',
          processed: 0,
          succeeded: 0,
          failed: 0,
        });

        const result: SubmissionResult = {
          success: true,
          productIds: unchanged.map((v) => v.uuid),
          errors: [],
        };

        onSuccess?.(result);
        return result;
      }

      // Build FormData for bulk submission
      const formData = new FormData();

      // Generate or use existing productGroupId - this links all variants together as siblings
      const productGroupId = product.id || uuidv4();

      formData.append('data', JSON.stringify({
        operations,
        productGroupId,
        familyId: product.familyId,
        familySlug: product.familySlug,
      }));

      // Add files to FormData (both pending files and extracted base64 files)
      for (const upload of allPendingFiles) {
        const { file, type, variantUuid } = upload;
        const ext = file.name.split('.').pop()?.toLowerCase() || 'png';

        if (type === 'thumbnail') {
          formData.append('images', file, `thumbnail_${variantUuid}.${ext}`);
        } else if (type === 'material') {
          formData.append('images', file, `image_${variantUuid}.${ext}`);
        } else if (type === 'plank') {
          formData.append('images', file, file.name);
        } else if (type === 'originalImage') {
          formData.append('images', file, `image_${variantUuid}.${ext}`);
        } else if (type === 'model') {
          formData.append('materials', file, `material_${variantUuid}.${ext}`);
        } else if (type === 'usdz') {
          // iOS AR Quick Look asset. Backend bulk regex parses `usdz_{uuid}.{ext}`
          // (see pms-api-v2 pms-product-v2.service.ts) and uploads with
          // Content-Type `model/vnd.usdz+zip` regardless of browser-reported MIME.
          formData.append('materials', file, `usdz_${variantUuid}.${ext}`);
        }
      }

      // Add material images from uploadedPlankImages state.
      // Everything here is a plank group — single-mode seamless textures go
      // through extractedFiles with type 'material'. Filename is always
      // `{groupId}_{index}.{ext}`; the API plank regex matches both the new
      // `material_<hex>` and legacy UUID-format groupIds.
      if (uploadedPlankImages) {
        for (const [groupId, files] of Object.entries(uploadedPlankImages)) {
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
            formData.append('images', file, `${groupId}_${i}.${ext}`);
          }
        }
      }

      // Add gallery image uploads keyed by variant uuid.
      // Backend matches filename pattern "gallery_{uuid}_{index}.{ext}" and
      // appends the uploaded CDN URLs to productData.galleryImages during bulk
      // processing. Only variants that are part of this submission (create or
      // update ops) need their gallery files forwarded.
      const submittedUuids = new Set(
        operations
          .filter((op) => op.operation !== 'delete')
          .map((op) => (op as any).uuid || (op.data as any)?.uuid)
          .filter(Boolean) as string[],
      );
      for (const variant of variants) {
        if (!submittedUuids.has(variant.uuid)) continue;
        const files = variant._galleryUploads ?? [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
          formData.append('images', file, `gallery_${variant.uuid}_${i}.${ext}`);
        }
      }

      // Submit bulk operations
      const batchResponse = await productV2Api.bulkOperations(formData);

      if (!batchResponse.batchId) {
        throw new Error('Backend did not return batchId');
      }

      updateProgress({
        batchId: batchResponse.batchId,
      });

      // Poll for completion
      const finalStatus = await productV2Api.pollBulkStatus(
        batchResponse.batchId,
        (status) => {
          // Check for abort
          if (abortControllerRef.current?.signal.aborted) {
            throw new Error('Submission cancelled');
          }

          // Update progress based on status
          const inProgress = (status.summary?.pending || 0) +
            (status.summary?.processing || 0) +
            (status.summary?.generating_seamless || 0);
          const completed = (status.summary?.completed || 0) + (status.summary?.failed || 0);

          updateProgress({
            processed: completed,
            succeeded: status.summary?.completed || 0,
            failed: status.summary?.failed || 0,
            currentItem: `Processing ${completed}/${totalOperations}...`,
            jobs: mergeJobStatus(status.jobs as any),
          });
        },
        300,
        2000,
        batchResponse.totalJobs
      );

      // Build result
      const errors: Array<{ uuid?: string; message: string }> = [];

      if (finalStatus.jobs) {
        for (const job of finalStatus.jobs) {
          if (job.status === 'failed') {
            errors.push({
              uuid: job.uuid,
              message: job.error || 'Unknown error',
            });
          }
        }
      }

      const succeeded = finalStatus.summary?.completed || 0;
      const failed = finalStatus.summary?.failed || 0;

      updateProgress({
        status: failed > 0 ? 'error' : 'success',
        processed: succeeded + failed,
        succeeded,
        failed,
        jobs: mergeJobStatus(finalStatus.jobs as any),
      });

      const result: SubmissionResult = {
        success: failed === 0,
        productIds: [
          ...created.map((v) => v.uuid),
          ...modified.map((v) => v.uuid),
        ],
        errors,
        batchId: batchResponse.batchId,
        jobs: mergeJobStatus(finalStatus.jobs as any),
      };

      // Clear pending files on success
      if (result.success) {
        clearPendingFiles();
      }

      // Grout v2: have the grout layers made now (tile photos and sizes are
      // final once the batch is done), so the first shopper to change the
      // grout does not wait for them. The visualizer asks too if this fails.
      const groutUuid = result.productIds.find((uuid) => {
        const v = variants.find((x) => x.uuid === uuid);
        return v?.visualization?.type === 'surface'
          && (productGroutEditable(product) ?? !!v.visualization?.surface?.groutEditable);
      });
      if (groutUuid) {
        Promise.resolve()
          .then(() => productV2Api.requestGroutLayers(groutUuid))
          .catch((e) => console.warn('[grout] could not queue grout layers', e));
      }

      onSuccess?.(result);
      return result;
    } catch (err: any) {
      const error = err instanceof Error ? err : new Error(String(err));

      updateProgress({
        status: 'error',
        error: error.message,
      });

      onError?.(error);

      return {
        success: false,
        productIds: [],
        errors: [{ message: error.message }],
      };
    }
  }, [product, variants, family, onSuccess, onError, updateProgress, clearPendingFiles]);

  // ─────────────────────────────────────────────────────────────────────────
  // Cancel
  // ─────────────────────────────────────────────────────────────────────────

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    updateProgress({
      status: 'idle',
      error: 'Submission cancelled',
    });
  }, [updateProgress]);

  // ─────────────────────────────────────────────────────────────────────────
  // Reset
  // ─────────────────────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    setProgress(createDefaultSubmissionProgress());
    clearPendingFiles();
  }, [clearPendingFiles]);

  // ─────────────────────────────────────────────────────────────────────────
  // Return
  // ─────────────────────────────────────────────────────────────────────────

  return {
    progress,
    submit,
    cancel,
    reset,
    setPendingFile,
    getPendingFile,
  };
}

export default useProductSubmissionV3;
